/**
 * F-097 — Splash with REAL in-game Earth + planet stack (mini Three.js scene).
 *
 * 사용자 피드백: "실제 인게임이랑 완전 똑같이". 이전 2D canvas 모방으로는
 * MeshToonMaterial · ShaderMaterial atmosphere · cel outline · sprite face
 * 를 절대 재현 불가 → 실제 createEarth · createPlanet 빌더를 그대로 호출
 * 해 mini Three.js scene 으로 splash 안에서 렌더 (start-scene.ts).
 *
 * 부가 기능: iOS audio unlock 의 click gate 유지. 데스크톱 (ctx 'running')
 * 은 splash 자체 미생성.
 */

import type { AudioSystem } from "../audio/system.ts";
import { createStartScene, type StartSceneHandle } from "./start-scene.ts";

export interface AudioPromptOptions {
  audio: AudioSystem;
  root?: HTMLElement;
}

export function mountAudioPrompt(opts: AudioPromptOptions): {
  detach: () => void;
} {
  const root = opts.root ?? document.body;

  if (opts.audio.diagnostics().ctxState === "running") {
    return { detach: () => undefined };
  }

  const overlay = document.createElement("div");
  overlay.setAttribute("data-testid", "audio-prompt");
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", "Cosmic Suika — start game");
  overlay.className = [
    "fixed",
    "inset-0",
    "z-50",
    "flex",
    "flex-col",
    "items-center",
    "justify-center",
    "gap-5",
    "bg-cosmic-deep/85",
    "backdrop-blur-md",
    "transition-opacity",
    "duration-500",
    "select-none",
  ].join(" ");

  const title = document.createElement("h1");
  title.setAttribute("data-testid", "splash-title");
  title.textContent = "COSMIC SUIKA";
  title.className = [
    "text-4xl",
    "sm:text-5xl",
    "font-display",
    "font-semibold",
    "tracking-wider",
    "text-white",
    "drop-shadow-[0_0_24px_rgba(125,97,255,0.6)]",
  ].join(" ");

  const heroBox = document.createElement("div");
  heroBox.setAttribute("data-testid", "splash-hero");
  heroBox.className = "relative flex items-center justify-center";

  // Mini Three.js scene — game 의 createEarth + createPlanet + lighting 그대로.
  // 해상도는 화면 작은 변에 맞춰 selection.
  const heroSize = computeHeroSize();
  let sceneHandle: StartSceneHandle | null = null;
  try {
    sceneHandle = createStartScene({
      width: heroSize.width,
      height: heroSize.height,
    });
    heroBox.appendChild(sceneHandle.canvas);
  } catch {
    // WebGL 사용 불가능한 환경 (test/headless). overlay 는 여전히 동작 —
    // 빈 hero 영역만 남고 button 은 그대로.
  }

  const subtitle = document.createElement("p");
  subtitle.textContent = "지구를 구하라";
  subtitle.className = "text-base text-slate-300 font-display";

  const button = document.createElement("button");
  button.type = "button";
  button.setAttribute("data-testid", "audio-prompt-button");
  button.textContent = "🚀  게임 시작";
  button.className = [
    "px-10",
    "py-4",
    "rounded-full",
    "bg-cosmic-glow",
    "text-white",
    "text-xl",
    "font-display",
    "font-semibold",
    "shadow-2xl",
    "ring-4",
    "ring-cosmic-glow/30",
    "transition",
    "active:scale-95",
    "animate-pulse",
    "pointer-events-auto",
    "cursor-pointer",
  ].join(" ");

  overlay.appendChild(title);
  overlay.appendChild(heroBox);
  overlay.appendChild(subtitle);
  overlay.appendChild(button);

  let removed = false;
  const detach = (): void => {
    if (removed) return;
    removed = true;
    sceneHandle?.dispose();
    overlay.remove();
  };

  const dismissWithFade = (): void => {
    overlay.classList.add("opacity-0", "pointer-events-none");
    sceneHandle?.pause();
    window.setTimeout(detach, 500);
  };

  const onClick = (e: Event): void => {
    e.preventDefault();
    e.stopPropagation();
    opts.audio.unlockNow();
    window.setTimeout(() => {
      if (opts.audio.diagnostics().ctxState === "running") {
        dismissWithFade();
      } else {
        opts.audio.unlockNow();
        dismissWithFade();
      }
    }, 100);
  };
  button.addEventListener("click", onClick);
  overlay.addEventListener("click", onClick);

  const poll = window.setInterval(() => {
    if (removed) {
      window.clearInterval(poll);
      return;
    }
    if (opts.audio.diagnostics().ctxState === "running") {
      window.clearInterval(poll);
      dismissWithFade();
    }
  }, 500);

  root.appendChild(overlay);

  return {
    detach: () => {
      window.clearInterval(poll);
      detach();
    },
  };
}

function computeHeroSize(): { width: number; height: number } {
  if (typeof window === "undefined") return { width: 360, height: 360 };
  const minSide = Math.min(window.innerWidth, window.innerHeight);
  // Splash 는 화면 단변의 ~65%. 정사각 구도로 지구 + 둘러싼 행성이
  // 세로 압박 없이 자연스럽게 frame 안에 들어옴.
  const target = Math.min(440, Math.max(280, Math.round(minSide * 0.65)));
  return { width: target, height: target };
}
